/**
 * Workshop Booking — Location flow: state pre-filter, city subset, distance.
 * Plan: never iterate over all 33k cities; state centroids first, then cities within those states.
 */

(function () {
  const DEG2RAD = Math.PI / 180;
  const EARTH_RADIUS_KM = 6371;

  /** Equirectangular approximation (fast, good for near distances). */
  function distanceEquirectangular(lat1, lng1, lat2, lng2) {
    const x = (lng2 - lng1) * DEG2RAD * Math.cos((lat1 + lat2) * DEG2RAD / 2);
    const y = (lat2 - lat1) * DEG2RAD;
    return Math.sqrt(x * x + y * y) * EARTH_RADIUS_KM;
  }

  /** Haversine (accurate for any distance). */
  function distanceHaversine(lat1, lng1, lat2, lng2) {
    const dLat = (lat2 - lat1) * DEG2RAD;
    const dLng = (lng2 - lng1) * DEG2RAD;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_KM * c;
  }

  /** Use equirectangular under 100 km, haversine otherwise. */
  function distanceKm(lat1, lng1, lat2, lng2) {
    const d = distanceEquirectangular(lat1, lng1, lat2, lng2);
    if (d < 100) return d;
    return distanceHaversine(lat1, lng1, lat2, lng2);
  }

  /**
   * State pre-filter: from (lat, lng) return list of state_codes to include (nearest 1–3).
   * statesJson: array of { state_code, centroid: { lat, lng }, bbox?: {...} }
   */
  function getNearestStateCodes(lat, lng, statesJson) {
    if (!Array.isArray(statesJson) || statesJson.length === 0) return [];
    const withDist = statesJson.map((s) => {
      const c = s.centroid || s;
      const clat = c.lat ?? s.lat;
      const clng = c.lng ?? s.lng;
      if (clat == null || clng == null) return { state_code: s.state_code, distanceKm: Infinity };
      return {
        state_code: s.state_code,
        distanceKm: distanceKm(lat, lng, clat, clng),
        bbox: s.bbox
      };
    });
    withDist.sort((a, b) => a.distanceKm - b.distanceKm);
    const nearest = withDist[0];
    if (!nearest) return [];
    const selected = [nearest.state_code];
    // Include 2nd state if within ~80 km of 1st
    if (withDist[1] && withDist[1].distanceKm - nearest.distanceKm < 80) {
      selected.push(withDist[1].state_code);
    }
    if (withDist[2] && withDist[2].distanceKm - nearest.distanceKm < 80) {
      selected.push(withDist[2].state_code);
    }
    return selected;
  }

  /**
   * From (lat, lng), state pre-filter, and cities list (with state_code, lat, lng, city_key),
   * return top N city_keys by distance. Cities must be in selectedStateCodes.
   */
  function getNearbyCityKeys(lat, lng, cities, selectedStateCodes, N) {
    if (!Array.isArray(cities) || selectedStateCodes.length === 0) return [];
    const stateSet = new Set(selectedStateCodes.map((c) => String(c).toUpperCase()));
    const inStates = cities.filter((c) => stateSet.has(String((c.state_code || '').toUpperCase())));
    const withDist = inStates.map((c) => ({
      city_key: c.city_key,
      distanceKm: distanceKm(lat, lng, c.lat, c.lng),
      city: c.city,
      state_code: c.state_code
    }));
    withDist.sort((a, b) => a.distanceKm - b.distanceKm);
    return withDist.slice(0, N || 10).map((x) => x.city_key);
  }

  /**
   * ResolvedLocation shape: { lat, lng, city?, state?, state_code?, country, source }.
   */
  function normalizeResolvedLocation(obj) {
    const lat = typeof obj.lat === 'number' ? obj.lat : parseFloat(obj.lat);
    const lng = typeof obj.lng === 'number' ? obj.lng : parseFloat(obj.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      city: obj.city != null ? String(obj.city).trim() : undefined,
      state: obj.state != null ? String(obj.state).trim() : undefined,
      state_code: obj.state_code != null ? String(obj.state_code).trim() : undefined,
      country: obj.country != null ? String(obj.country).trim() : 'US',
      source: obj.source || 'local_json'
    };
  }

  /**
   * Derive city_key from event (state + city) to match workshop-locations.json convention.
   * Format: us-{state_code}-{slug(city)}
   */
  function deriveEventCityKey(stateCode, city) {
    const sc = (stateCode || '').trim().toLowerCase().replace(/\s+/g, '-');
    const c = (city || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (!sc || !c) return '';
    return `us-${sc}-${c}`;
  }

  /**
   * Distance bucket index for Near locations UI.
   * Buckets: 0, (0–50], (50–100], (100–250], (250–500] km. >500 km → -1 (exclude).
   */
  function getDistanceBucketIndex(distanceKm) {
    const d = Number(distanceKm);
    if (Number.isNaN(d) || d < 0) return -1;
    if (d === 0) return 0;
    if (d <= 50) return 1;
    if (d <= 100) return 2;
    if (d <= 250) return 3;
    if (d <= 500) return 4;
    return -1;
  }

  /** Bucket labels: 0 km, (0–50] km, (50–100] km, (100–250] km, (250–500] km */
  var nearBucketLabels = ['0 km', '(0–50] km', '(50–100] km', '(100–250] km', '(250–500] km'];

  window.WorkshopLocationFlow = {
    distanceKm,
    distanceEquirectangular,
    distanceHaversine,
    getNearestStateCodes,
    getNearbyCityKeys,
    normalizeResolvedLocation,
    deriveEventCityKey,
    getDistanceBucketIndex,
    NEAR_BUCKET_LABELS: nearBucketLabels
  };
})();
